import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

const PUBLIC_URL_BASE = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
function getMainBundleHash() {
  // CRA production builds emit /static/js/main.<hash>.js
  // We use that hash as a stable cache buster so deployments always load fresh locale JSON.
  if (typeof document === 'undefined') return 'ssr';
  try {
    // eslint-disable-next-line no-restricted-syntax
    for (const s of Array.from(document.scripts || [])) {
      const src = s && s.src ? String(s.src) : '';
      const m = src.match(/\/static\/js\/main\.([a-f0-9]+)\.js/i);
      if (m && m[1]) return m[1];
    }
  } catch (e) {
    // ignore
  }
  return 'nohash';
}

const I18N_CACHE_BUST = getMainBundleHash();
const LOCALES_LOAD_PATH = `${PUBLIC_URL_BASE}/locales/{{lng}}/{{ns}}.json?v=${I18N_CACHE_BUST}`;

const loggedFailedLoadUrls = new Set();

// IMPORTANT:
// Only allow languages we actually ship under /public/locales.
// This prevents bad detections like "group" / "games" from the URL path, which
// would otherwise cause every t(...) to fall back to raw keys.
const SUPPORTED_LNGS = ['en-US', 'zh-CN', 'zh-TW'];

const CANONICAL_LNG = {
  en: 'en-US',
  'en-us': 'en-US',
  // Accept a few common aliases for Chinese
  zh: 'zh-CN',
  'zh-cn': 'zh-CN',
  cn: 'zh-CN',
  'zh-tw': 'zh-TW',
  tw: 'zh-TW',
};

function canonicalizeLanguage(lng) {
  if (!lng || typeof lng !== 'string') return lng;
  const normalized = lng.trim();
  const lower = normalized.toLowerCase();

  if (CANONICAL_LNG[lower]) return CANONICAL_LNG[lower];
  // Treat any English variant as en-US for now (en, en-GB, etc.)
  if (lower === 'en' || lower.startsWith('en-')) return 'en-US';
  // If it's not a supported language, force fallback.
  // (This fixes cases where the detector mistakenly returns a route segment like "group".)
  if (!SUPPORTED_LNGS.includes(normalized)) return 'en-US';
  return normalized;
}

const options = 
{
  // order and from where user language should be detected
  // DO NOT detect language from URL path/subdomain in this app, because our routes
  // begin with segments like /group/... and /games/... which are not languages.
  order: ['querystring', 'localStorage', 'cookie', 'navigator', 'htmlTag'],

  // Cache the detected language (so subsequent loads are stable).
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'],

  // other options:
  // // keys or params to lookup language from
  // lookupQuerystring: 'lng',
  // lookupCookie: 'i18next',
  // lookupLocalStorage: 'i18nextLng',
  // lookupSessionStorage: 'i18nextLng',
  // lookupFromPathIndex: 0,
  // lookupFromSubdomainIndex: 0,

  // // cache user language on
  // caches: ['localStorage', 'cookie'],
  // excludeCacheFor: ['cimode'], // languages to not persist (cookie, localStorage)

  // // optional expire and domain for set cookie
  // cookieMinutes: 10,

  // // optional htmlTag with lang attribute, the default is:
  // htmlTag: document.documentElement,

  // // optional set cookie options, reference:[MDN Set-Cookie docs](https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie)
  // cookieOptions: { path: '/', sameSite: 'strict' }
}

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next);
i18n.on('languageChanged', (lng) => {
  const canonical = canonicalizeLanguage(lng);
  if (canonical && canonical !== lng) {
    i18n.changeLanguage(canonical);
  }
});
i18n.on('failedLoading', (lng, ns, msg) => {
  console.warn('[i18n] failedLoading', { lng, ns, msg, loadPath: LOCALES_LOAD_PATH });
  try {
    const url = LOCALES_LOAD_PATH.replace('{{lng}}', lng).replace('{{ns}}', ns);
    if (loggedFailedLoadUrls.has(url)) return;
    loggedFailedLoadUrls.add(url);

    // On parse errors, the server is usually returning HTML (e.g. index.html fallback)
    // or an error page. Fetch the raw response and log a snippet for diagnosis.
    fetch(url, { cache: 'no-store' })
      .then(async (res) => {
        const text = await res.text();
        const headers = {};
        try {
          // eslint-disable-next-line no-restricted-syntax
          for (const [k, v] of res.headers.entries()) headers[k] = v;
        } catch (e) {
          // ignore
        }
        console.warn('[i18n] failedLoading response', {
          url,
          finalUrl: res.url,
          status: res.status,
          contentType: res.headers.get('content-type'),
          cacheControl: res.headers.get('cache-control'),
          etag: res.headers.get('etag'),
          age: res.headers.get('age'),
          via: res.headers.get('via'),
          headers,
          snippet: text.slice(0, 240),
        });
      })
      .catch((err) => {
        console.warn('[i18n] failedLoading response fetch failed', { url, err });
      });
  } catch (e) {
    // ignore diagnostics failures
  }
});

i18n
  .init(
    {
      backend: {
        // Ensure locale files load correctly even when the app is hosted
        // under a non-root PUBLIC_URL or on deep routes.
        loadPath: LOCALES_LOAD_PATH,
        // Better error logs: if the server returns HTML/empty bodies, show a snippet.
        parse: (data, lng, ns) => {
          try {
            return JSON.parse(data);
          } catch (err) {
            const resolvedLng = lng || 'unknown-lng';
            const resolvedNs = ns || 'unknown-ns';
            const resolvedUrl = LOCALES_LOAD_PATH.replace('{{lng}}', resolvedLng).replace('{{ns}}', resolvedNs);
            console.warn('[i18n] backend.parse failed', {
              loadPath: LOCALES_LOAD_PATH,
              lng: resolvedLng,
              ns: resolvedNs,
              url: resolvedUrl,
              snippet: String(data || '').slice(0, 240),
              err,
            });
            throw err;
          }
        },
      },
      detection: options,
      debug: process.env.NODE_ENV === 'development',
      supportedLngs: SUPPORTED_LNGS,
      load: 'currentOnly',
      // If the app ever ends up with "en" (or any other non-canonical alias),
      // ensure we still fall back to the canonical English bundle.
      fallbackLng: 'en-US',
      ns: ['translation'],
      defaultNS: 'translation',
      react: {
        useSuspense: true,
      },
    },
    () => {
      // Also normalize the startup language (covers the initial detection path).
      const canonical = canonicalizeLanguage(i18n.language);
      if (canonical && canonical !== i18n.language) {
        i18n.changeLanguage(canonical);
      }
      if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
        // Useful for debugging in DevTools: window.__i18n.language, window.__i18n.exists('general.slogan'), etc.
        window.__i18n = i18n;
      }
    }
  );

export default i18n;